// tokenStorage - JWT token persistence utilities
// Implements US-1.1: User Authentication with Development Mode
// Implements US-1.2: Viewer Guest Access
// Uses sessionStorage for security (tokens cleared on tab close)

const TOKEN_KEY = 'arbiter_auth_token';
const VIEWER_TOKEN_KEY = 'arbiter_viewer_token';

/**
 * STORE JWT token in sessionStorage
 * Token will be cleared when browser tab is closed
 *
 * @param token - JWT token to store
 */
export function storeToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store auth token:', error);
    throw new Error('Failed to save authentication. Please try again.');
  }
}

/**
 * RETRIEVE JWT token from sessionStorage
 * Returns null if no token stored
 *
 * @returns Stored JWT token or null
 */
export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve auth token:', error);
    return null;
  }
}

/**
 * REMOVE JWT token from sessionStorage
 * Used for logout functionality
 */
export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to clear auth token:', error);
  }
}

/**
 * CHECK if user is currently authenticated
 * Returns true if token exists (doesn't verify validity)
 *
 * @returns Boolean indicating if token exists
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

// ========================================
// VIEWER TOKEN FUNCTIONS
// ========================================

/**
 * STORE viewer JWT token in sessionStorage
 * Token will be cleared when browser tab is closed
 *
 * @param token - Viewer JWT token to store
 */
export function storeViewerToken(token: string): void {
  try {
    sessionStorage.setItem(VIEWER_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store viewer token:', error);
    throw new Error('Failed to save viewer session. Please try again.');
  }
}

/**
 * RETRIEVE viewer JWT token from sessionStorage
 * Returns null if no token stored
 *
 * @returns Stored viewer JWT token or null
 */
export function getViewerToken(): string | null {
  try {
    return sessionStorage.getItem(VIEWER_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve viewer token:', error);
    return null;
  }
}

/**
 * REMOVE viewer JWT token from sessionStorage
 * Used when viewer leaves workspace
 */
export function clearViewerToken(): void {
  try {
    sessionStorage.removeItem(VIEWER_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to clear viewer token:', error);
  }
}

/**
 * CHECK if viewer session exists
 * Returns true if viewer token exists (doesn't verify validity)
 *
 * @returns Boolean indicating if viewer token exists
 */
export function hasViewerSession(): boolean {
  return getViewerToken() !== null;
}
